import useSWR from "swr";
import { useWsmPlugin } from "../useWsmPlugin";
import { useCallback, useEffect, useMemo } from "react";
import { LocalEntityType } from "@WahooFitness/wsm-native/dist/esm/types/local_entity_type";
import { WorkoutAlertInputType } from "@WahooFitness/cloud-client-types";
import { useGuardedUpdater } from "@WahooFitness/wahoo-offline-mfe";
import { IWorkoutAlertSource } from "./useWorkoutAlertsSource";
import { t } from "@lingui/macro";

export const useWsmWorkoutAlertsSource = (
  workoutProfileExternalId: string,
  enabled: boolean
): IWorkoutAlertSource => {
  const { sensorManagerPlugin } = useWsmPlugin();
  const { update, updateInProgress } = useGuardedUpdater();

  const {
    data: workoutAlerts,
    isLoading: workoutAlertsAreLoadingInternal,
    error: workoutAlertsError,
    mutate: reloadWorkoutAlerts,
  } = useSWR(
    enabled && ["wsmWorkoutAlerts" as const, workoutProfileExternalId],
    async ([_key, externalId]) => {
      const alerts = (
        await sensorManagerPlugin.getWorkoutAlerts({ workoutProfileToken: externalId })
      )?.workoutAlerts;
      return alerts.filter((a) => a["#deleted"] !== true);
    }
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const listener = sensorManagerPlugin.addListener("cloudEntityChanged", (event) => {
      if (event.entityType === LocalEntityType.WorkoutAlert) {
        reloadWorkoutAlerts();
      }
    });
    return () => {
      (async () => (await listener)?.remove())();
    };
  }, [enabled, reloadWorkoutAlerts, sensorManagerPlugin]);

  const createWorkoutAlert = useCallback(
    async (alert: WorkoutAlertInputType) => {
      return update(
        async () => {
          const result = (
            await sensorManagerPlugin.createWorkoutAlert({
              workoutProfileToken: workoutProfileExternalId,
              workoutAlert: alert,
            })
          ).workoutAlert;
          return result;
        },
        { errorMessage: t`Failed to create workout alert. Please restart the app and try again.` }
      );
    },
    [sensorManagerPlugin, update, workoutProfileExternalId]
  );

  const editWorkoutAlert = useCallback(
    async (workoutAlertExternalId: string, edits: WorkoutAlertInputType) => {
      return update(
        async () => {
          const { completed } = await sensorManagerPlugin.editLocalEntity({
            entityType: LocalEntityType.WorkoutAlert,
            token: workoutAlertExternalId,
            edits,
          });
          if (completed) {
            const result = (
              await sensorManagerPlugin.getWorkoutAlert({ token: workoutAlertExternalId })
            ).workoutAlert;
            return result;
          } else {
            throw new Error("Failed to edit workout alert");
          }
        },
        {
          errorMessage: t`Something went wrong editing the alert. Please restart the app and try again.`,
        }
      );
    },
    [sensorManagerPlugin, update]
  );

  const deleteWorkoutAlert = useCallback(
    async (workoutAlertExternalId: string) => {
      return update(
        async () => {
          const { completed } = await sensorManagerPlugin.editLocalEntity({
            entityType: LocalEntityType.WorkoutAlert,
            token: workoutAlertExternalId,
            edits: { "#deleted": true },
          });
          if (!completed) {
            throw new Error("Failed to delete workout alert");
          }
          return completed;
        },
        {
          errorMessage: t`Something went wrong deleting the alert. Please restart the app and try again.`,
        }
      );
    },
    [sensorManagerPlugin, update]
  );

  const workoutAlertsAreLoading = useMemo(() => {
    return workoutAlertsAreLoadingInternal || updateInProgress;
  }, [updateInProgress, workoutAlertsAreLoadingInternal]);

  return {
    workoutAlerts,
    workoutAlertsAreLoading,
    workoutAlertsError,
    reloadWorkoutAlerts,
    createWorkoutAlert,
    editWorkoutAlert,
    deleteWorkoutAlert,
  };
};
